import { Component } from '@theme/component';
import { throttle } from '@theme/utilities';

/**
 * Product options scroller component with custom scrollbar and navigation controls
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} scrollbar
 * @property {HTMLElement} thumb
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
class ProductOptionsScrollerComponent extends Component {
  requiredRefs = ['wrapper'];

  /** @type {((e: Event) => void) | null} */
  #previousClickHandler = null;
  
  /** @type {((e: Event) => void) | null} */
  #nextClickHandler = null;

  /** @type {(() => void) | null} */
  #abortDrag = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLElement | null} */
  #tooltip = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.previous) {
      this.#previousClickHandler = (e) => {
        this.scrollPrevious(e);
      };
      this.refs.previous.addEventListener('click', this.#previousClickHandler);
    }

    if (this.refs.next) {
      this.#nextClickHandler = (e) => {
        this.scrollNext(e);
      };
      this.refs.next.addEventListener('click', this.#nextClickHandler);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateControls();
        this.#updateScrollbar();
      });
    });

    this.refs.wrapper.addEventListener('scroll', this.#handleScroll);

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.refs.wrapper);

    if (this.refs.thumb && this.refs.scrollbar) {
      this.refs.thumb.addEventListener('mousedown', this.#handleThumbMouseDown);
      this.refs.scrollbar.addEventListener('click', this.#handleScrollbarClick);
    }

    this.#setupTooltips();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Abort any ongoing drag operation
    if (this.#abortDrag) {
      this.#abortDrag();
      this.#abortDrag = null;
    }
    
    this.refs.wrapper.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver?.disconnect();
    
    if (this.refs.previous && this.#previousClickHandler) {
      this.refs.previous.removeEventListener('click', this.#previousClickHandler);
    }
    
    if (this.refs.next && this.#nextClickHandler) {
      this.refs.next.removeEventListener('click', this.#nextClickHandler);
    }
    
    if (this.refs.thumb) {
      this.refs.thumb.removeEventListener('mousedown', this.#handleThumbMouseDown);
    }
    
    if (this.refs.scrollbar) {
      this.refs.scrollbar.removeEventListener('click', this.#handleScrollbarClick);
    }

    this.#removeTooltip();
  }

  /**
   * Scroll to the previous item
   * @param {Event} event
   */
  scrollPrevious(event) {
    event?.preventDefault();
    const { wrapper } = this.refs;
    
    const items = wrapper.querySelectorAll('a');
    if (!items.length) return;
    
    const firstItem = items[0];
    if (!firstItem) return;
    
    const itemWidth = firstItem.offsetWidth;
    const gap = parseFloat(getComputedStyle(wrapper).gap) || 0;
    const scrollAmount = itemWidth + gap;
    
    wrapper.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }

  /**
   * Scroll to the next item
   * @param {Event} event
   */
  scrollNext(event) {
    event?.preventDefault();
    const { wrapper } = this.refs;
    
    const items = wrapper.querySelectorAll('a');
    if (!items.length) return;
    
    const firstItem = items[0];
    if (!firstItem) return;
    
    const itemWidth = firstItem.offsetWidth;
    const gap = parseFloat(getComputedStyle(wrapper).gap) || 0;
    const scrollAmount = itemWidth + gap;

    wrapper.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  /**
   * Update visibility and state of navigation controls
   */
  #updateControls = () => {
    const { wrapper, previous, next, controls } = this.refs;
    
    if (!wrapper) return;

    const needsScroll = wrapper.scrollWidth > wrapper.clientWidth;
    const atStart = wrapper.scrollLeft <= 1;
    const atEnd = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 1;

    if (controls instanceof HTMLElement) {
      if (needsScroll) {
        controls.classList.add('is-visible');
      } else {
        controls.classList.remove('is-visible');
      }
    }
    
    if (previous) {
      previous.disabled = atStart;
    }
    
    if (next) {
      next.disabled = atEnd;
    }

    if (this.refs.scrollbar) {
      if (needsScroll) {
        this.refs.scrollbar.classList.add('is-visible');
      } else {
        this.refs.scrollbar.classList.remove('is-visible');
      }
    }
  };

  /**
   * Update scrollbar thumb position and size
   */
  #updateScrollbar = () => {
    const { wrapper, thumb, scrollbar } = this.refs;
    
    if (!wrapper || !thumb || !scrollbar) return;

    const scrollRatio = wrapper.clientWidth / wrapper.scrollWidth;
    // Minimum thumb width: 10% of scrollbar width or 20px, whichever is larger
    const minThumbWidth = Math.max(scrollbar.clientWidth * 0.1, 20);
    const thumbWidth = Math.max(scrollbar.clientWidth * scrollRatio, minThumbWidth);
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    const scrollProgress = maxScroll > 0 ? wrapper.scrollLeft / maxScroll : 0;
    const maxThumbPosition = scrollbar.clientWidth - thumbWidth;
    const thumbPosition = maxThumbPosition * scrollProgress;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbPosition}px)`;
  };

  /**
   * Handle scroll events (throttled)
   */
  #handleScroll = throttle(() => {
    this.#updateControls();
    this.#updateScrollbar();
  }, 16);

  /**
   * Handle resize events
   */
  #handleResize = () => {
    this.#updateControls();
    this.#updateScrollbar();
  };

  /**
   * Handle thumb mousedown for dragging
   * @param {MouseEvent} event
   */
  #handleThumbMouseDown = (event) => {
    event.preventDefault();
    
    const { wrapper, thumb, scrollbar } = this.refs;
    if (!wrapper || !thumb || !scrollbar) return;

    const startX = event.clientX;
    const startScrollLeft = wrapper.scrollLeft;
    const scrollbarRect = scrollbar.getBoundingClientRect();
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    
    /**
     * @param {MouseEvent} e
     */
    const onMouseMove = (e) => {
      // Check if component is still connected
      if (!this.isConnected) {
        cleanup();
        return;
      }
      
      const deltaX = e.clientX - startX;
      const scrollRatio = maxScroll / (scrollbarRect.width - thumb.offsetWidth);
      const newScrollLeft = startScrollLeft + (deltaX * scrollRatio);
      
      wrapper.scrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      this.#abortDrag = null;
    };

    const onMouseUp = () => {
      cleanup();
    };

    // Store cleanup function so it can be called from disconnectedCallback
    this.#abortDrag = cleanup;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  /**
   * Handle scrollbar click to jump to position
   * @param {MouseEvent} event
   */
  #handleScrollbarClick = (event) => {
    if (event.target === this.refs.thumb) return;
    
    const { wrapper, scrollbar, thumb } = this.refs;
    if (!wrapper || !scrollbar || !thumb) return;

    const scrollbarRect = scrollbar.getBoundingClientRect();
    const clickPosition = event.clientX - scrollbarRect.left;
    const scrollRatio = clickPosition / scrollbarRect.width;
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    
    wrapper.scrollTo({
      left: maxScroll * scrollRatio,
      behavior: 'smooth'
    });
  };

  /**
   * Setup tooltip event listeners for option links (swatches and text labels)
   */
  #setupTooltips = () => {
    const tooltipTargets = this.refs.wrapper.querySelectorAll('a[data-tooltip-image]');

    tooltipTargets.forEach((target) => {
      target.addEventListener('mouseenter', this.#showTooltip);
      target.addEventListener('mouseleave', this.#hideTooltip);
    });
  };

  /**
   * Show tooltip for a button
   * @param {MouseEvent} event
   */
  #showTooltip = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const image = target.dataset.tooltipImage;
    const title = target.dataset.tooltipTitle;

    if (!image || !title) return;

    // Create tooltip if it doesn't exist
    if (!this.#tooltip) {
      this.#tooltip = document.createElement('div');
      this.#tooltip.className = 'product-options__tooltip';
      document.body.appendChild(this.#tooltip);
    }

    // Set tooltip content with loading state
    this.#tooltip.innerHTML = `
      <div class="product-options__tooltip-image">
        <div class="product-options__tooltip-loader"></div>
        <img src="${image}" alt="${title}" style="display: none;" />
      </div>
      <span>${title}</span>
    `;

    // Position tooltip immediately with fixed dimensions
    this.#positionTooltip(target);

    // Show tooltip
    this.#tooltip.classList.add('is-visible');

    // Handle image load
    const img = this.#tooltip.querySelector('img');
    const loader = this.#tooltip.querySelector('.product-options__tooltip-loader');
    
    if (img) {
      img.onload = () => {
        if (loader) loader.style.display = 'none';
        img.style.display = 'block';
      };
      
      img.onerror = () => {
        if (loader) loader.style.display = 'none';
        img.style.display = 'block';
      };
    }
  };

  /**
   * Hide tooltip
   */
  #hideTooltip = () => {
    if (this.#tooltip) {
      this.#tooltip.classList.remove('is-visible');
    }
  };

  /**
   * Position tooltip above the target element
   * @param {HTMLElement} target
   */
  #positionTooltip = (target) => {
    if (!this.#tooltip) return;

    const targetRect = target.getBoundingClientRect();
    
    // Fixed tooltip dimensions (matching CSS)
    const tooltipWidth = 200;
    const tooltipHeight = 240; // Approximate: 200px image + padding + text
    
    // Position above the button, centered
    let left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
    const top = targetRect.top - tooltipHeight - 20; // 20px gap

    // Keep tooltip within viewport
    const margin = 10;
    if (left < margin) {
      left = margin;
    } else if (left + tooltipWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltipWidth - margin;
    }

    this.#tooltip.style.left = `${left}px`;
    this.#tooltip.style.top = `${top}px`;
  };

  /**
   * Remove tooltip from DOM
   */
  #removeTooltip = () => {
    if (this.#tooltip) {
      this.#tooltip.remove();
      this.#tooltip = null;
    }
  };
}

if (!customElements.get('product-options-scroller')) {
  customElements.define('product-options-scroller', ProductOptionsScrollerComponent);
}
